import { useLocation } from 'react-router';

import { REDIRECT_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

interface GetRedirectRouteParams {
  pathname: string;
  search?: string;
  to: string;
}

/**
 * Calculates the redirect route. Compatible with actions and loaders.
 *
 * Returns a basename-relative path. React Router prepends the configured
 * basename for router-native navigations (`redirect()`), and the encoded
 * `redirectTo` value stays relative so it round-trips safely whether it is
 * later consumed by `redirect()`/`navigate()` or by a hard nav (useHardRedirect).
 * @returns the redirect route
 */
export function getRedirectRoute({
  pathname,
  search = '',
  to,
}: GetRedirectRouteParams): string {
  return `${to}?${REDIRECT_QUERY_KEY}=${encodeURIComponent(`${pathname}${search}`)}`;
}

type GetRouteWithRedirect = (to: string) => string;

/**
 * Custom hook that returns a function to calculate the redirect route.
 * @param withoutRedirectParam - If true, does not add the redirect query param to the route
 * @returns A function that returns the redirect route
 */
export function useRouteWithRedirect(
  withoutRedirectParam: boolean = false
): GetRouteWithRedirect {
  const { pathname, search } = useLocation();

  const getRouteWithRedirect: GetRouteWithRedirect = (to: string) => {
    return withoutRedirectParam
      ? to
      : getRedirectRoute({
          // `location.pathname` is basename-relative under React Router's native
          // basename, which is exactly what we store: redirect values stay relative
          // and the basename is re-attached only at hard-nav time (useHardRedirect).
          pathname,
          search,
          to,
        });
  };

  return getRouteWithRedirect;
}
